import { actionCreators } from '../../store/actions';
import ReduxActions from '../../store/ReduxActions';
import { Franchise, InitialDataResponseInterface } from '../exports/Interfaces';
import BackendApiClient from './BackendApiClient';
import { DataWrappedResponse, Response } from '../exports/Types';
import DomainResource from '../resources/DomainResource';
import AuthorizationTokenStorage from '../auth/AuthorizationTokenStorage';
import collect from 'collect.js';
import ImageResource from '../resources/ImageResource';
import WindowResource from '../resources/WindowResource';
import StorefrontResource from '../resources/StorefrontResource';

export type GetFranchiseParams =
  | {
      franchise_slug: string;
    }
  | { domain: string };

class InitialLoadingManager {
  private subdomain: string = DomainResource.extractSubdomain();
  private domain: string = DomainResource.getHostname();

  public async getFranchiseData(hideLoading: boolean = false): Promise<DataWrappedResponse<Franchise>> {
    if (!hideLoading) {
      this.toggleLoading(true);
    }

    const request = await BackendApiClient.request({
      method: 'GET',
      url: '/franchises',
      params: this.getFranchiseParams(),
    });

    if (!hideLoading) {
      this.toggleLoading(false);
    }

    const { response, success } = request;

    if (success) {
      ReduxActions.dispatch(actionCreators.initialData.setFranchise(response.data.data));
    }

    return request;
  }

  public async getDefaultCompanySlug(): Promise<null | string> {
    const { success, response } = await this.getFranchiseData(true);

    if (!success) {
      return null;
    }

    return collect(response.data.data?.companies).first()?.slug;
  }

  public async getCompanyData(
    slug: string = null,
    hideLoading: boolean = false,
    shouldRetry: boolean = true,
  ): Promise<Response<InitialDataResponseInterface>> {
    if (!hideLoading) {
      this.toggleLoading(true);
    }

    if (!slug) {
      slug = await this.getDefaultCompanySlug();
    }

    const params = this.getFranchiseParams();

    const request = await BackendApiClient.request({
      method: 'GET',
      url: `/companies/${slug}/data`,
      params,
    });
    if (!hideLoading) {
      this.toggleLoading(false);
    }

    const { response, success, status } = request;

    if (success) {
      this.saveDataRedux(response?.data);
    }

    if (status === 404) {
      if (slug !== null || !shouldRetry) {
        return this.handleCompanyNotFound();
      }

      return this.getCompanyData(await this.getDefaultCompanySlug(), hideLoading, false);
    }

    return request;
  }

  public async getInitialData(
    slug: string = null,
    hideLoading: boolean = false,
    shouldRetry: boolean = true,
  ): Promise<Response<InitialDataResponseInterface>> {
    if (!hideLoading) {
      this.toggleLoading(true);
    }

    if (!slug) {
      slug = await this.getDefaultCompanySlug();
    }

    const params = this.getFranchiseParams();

    const request = await BackendApiClient.request({
      method: 'GET',
      url: `/companies/${slug}/initial-data`,
      params,
    });
    if (!hideLoading) {
      this.toggleLoading(false);
    }

    const { response, success, status } = request;

    if (success) {
      this.saveDataRedux(response?.data);
    }

    if (status === 404) {
      if (slug !== null || !shouldRetry) {
        return this.handleCompanyNotFound();
      }

      return this.getInitialData(await this.getDefaultCompanySlug(), hideLoading, false);
    }

    return request;
  }

  public init(slug: string = null, hideLoading: boolean = false): Promise<Response<InitialDataResponseInterface>> {
    if (AuthorizationTokenStorage.getToken()) {
      return this.getInitialData(slug, hideLoading);
    }

    return this.getCompanyData(slug, hideLoading);
  }

  private saveDataRedux(response: InitialDataResponseInterface): void {
    ImageResource.preloadImages(collect(response.company?.pictures).take(3).toArray()).finally(() => {
      ReduxActions.batchActions([
        actionCreators.initialData.setAllData(response),
        actionCreators.profile.setProfile(response?.profile),
        actionCreators.paymentOptions.setPaymentOptions(response?.payment_options),
      ]);
    });
  }

  private handleCompanyNotFound(): Response<null> {
    ReduxActions.dispatch(
      actionCreators.initialData.updateCompany({
        slug: null,
      }),
    );

    if (window.location.pathname !== '/404') {
      WindowResource.replace('/404');
    }

    return {
      success: false,
      status: 404,
      response: null,
    };
  }

  private toggleLoading(loading?: boolean) {
    ReduxActions.dispatch(actionCreators.initialData.toggleLoading(loading));
  }

  public getFranchiseParams(): GetFranchiseParams {
    if (this.subdomain && !StorefrontResource.isStorefront()) {
      return { franchise_slug: this.subdomain };
    }

    return { domain: this.domain };
  }
}

export default new InitialLoadingManager();
